import React, { useContext, useEffect } from "react";
import {
  Autocomplete,
  Checkbox,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { ClassicTableDatasheet } from "../../../utils/components/Styles";
import moment from "moment";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { DatasheetContext } from "./datasheet.context.js";
import {
  accuracyFormat,
  parseRange,
} from "../../../utils/components/accuracyandlcandrangesshow.js";
import unitConvertor, { getBaseUnit } from "../../../utils/components/unitConversion/unitConvertor.js";

export default function DetailsOfCalStandards(props) {
  const {
    state: {
      standardMasterArray,
      selectedStandardIds,
      selectedStandardMasters,
      expiredStandardNames,
      datasheetDetails,
      selectedMastersIds,
      selectedMonitoringIds,
      staticTables,
    },
    dispatch,
  } = useContext(DatasheetContext);

    const masterEQPRangeFiltration = staticTables?.[0]?.defaultConfiguration?.masterEQPRangeFiltration || false;

  //validation Function
  const validateStandards = (type) => {
    let counter = 1;
    if (type === 1) {
      let expiredStandards = [];
      standardMasterArray.map((standard) => {
        if (moment().isAfter(standard.validUpto)) {
          expiredStandards.push(
            `${counter}. ${standard.stId} (${standard.standardName})`
          );
          counter = counter + 1;
        }
      });
      return expiredStandards;
    }
  };

  const updateSelectedStandardMasters = (selectedStandardIds,selectedMonitoringIds) => {
    // fun: assign standard master data from standardMasterArray to selectedStandardMasters based on selectedStandardIds
    let _selectedStandardIds = [...selectedStandardIds, ...selectedMonitoringIds]
      .map((id) => Number(id))
      .filter((id) => id > 0);

    if (!standardMasterArray || standardMasterArray?.length === 0) return;

    let tempSelectedStandardMasters = [];
    _selectedStandardIds.forEach((id) => {
      let standardMaster = standardMasterArray.find(
        (standard) => standard.id == id
      );
      if (standardMaster) tempSelectedStandardMasters.push(standardMaster);
    });

    // set selectedStandardMasters
    dispatch({
      field: "selectedStandardMasters",
      value: [...tempSelectedStandardMasters],
    });
  };

  useEffect(() => {
    updateSelectedStandardMasters(selectedStandardIds,selectedMonitoringIds);
  }, [selectedStandardIds,selectedMonitoringIds, standardMasterArray]);

  useEffect(() => {
    if (selectedStandardMasters?.length > 0) {
      const expiredItems = selectedStandardMasters.filter(
        (row) =>
          moment(datasheetDetails?.calibrationDate).isAfter(
            moment(row.validUpto)
          ) &&
          moment(datasheetDetails?.calibrationDate).isSameOrAfter(
            moment(row.validUpto)
          )
      );

      // setUnValidMasterCount(expiredItems.length);

      let allStandardNames = expiredItems.map(
        (item, index) => `${index + 1}. ${item.stId} (${item.standardName})`
      );

      dispatch({
        field: "expiredStandardNames",
        value: allStandardNames,
      });
    }
  }, [selectedStandardMasters, datasheetDetails?.calibrationDate]);

  return (
    <div>
      <>
        {props.isPrinting ? (
          <Typography variant="body1" component="div" sx={{ mb: 0, textAlign: "center" }}>
            Details of Master EQP Used For Calibration
          </Typography>
        ) : (
          <Divider sx={{ my: 2 }}>
            <Chip
              label="Details of Master EQP Used For Calibration"
              variant="outlined"
              sx={{ fontSize: "1.1rem", fontWeight: "bold" }}
            />
          </Divider>
        )}

        {!props.isPrinting && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              margin: "auto",
              marginBottom: "5px",
            }}
          >
            <Autocomplete
              multiple
              id="outlined-basic"
              size="small"
              className="Listinputcss"
              style={{ minWidth: "350px" }}
              disableCloseOnSelect
              options={(() => {
                const filtered = standardMasterArray?.filter((row) =>
                  moment(datasheetDetails?.calibrationDate).isBetween(
                    row.calDate,
                    row.validUpto,
                    null,
                    "[]"
                  )
                );

                return (
                  Object.values(
                    filtered?.reduce((acc, row) => {
                      acc[row.stId] =
                        !acc[row.stId] || row.id > acc[row.stId].id
                          ? row
                          : acc[row.stId];
                      return acc;
                    }, {})
                  ) || []
                );
              })()}
              value={
                standardMasterArray?.filter((ele) =>
                  selectedMastersIds?.split(",")?.map(Number)?.includes(ele.id)
                ) || null
              }
              getOptionLabel={(option) =>
                `${option.stId} : ${option.standardName}` || ""
              }
              renderOption={(props, option, { selected }) => {
                // ---------- Helpers ----------

            const getUnit = (value) => {
                if (typeof value !== "string") return "";
                const match = value.match(/#([^#|]+)/);
                return match ? match[1].trim() : "";
              };


                const parseRanges = (rangeString = "") =>
                  (rangeString || "")
                    .split("||")
                    .map((r) => r.trim())
                    .filter(Boolean)
                    .map((range) => {
                      if (range.includes("|")) {
                        const [minPart = "", maxPart = ""] = range
                          .split("|")
                          .map((x) => x.trim());
                        const [minValue = "", minUnit = ""] =
                          minPart.split("#");
                        const [maxValue = "", maxUnit = ""] =
                          maxPart.split("#");
                        return {
                          min: parseFloat(minValue) || 0,
                          max: parseFloat(maxValue) || 0,
                          unit: minUnit?.trim() || maxUnit?.trim() || "",
                        };
                      } else {
                        const [value = "", unit = ""] = range.split("#");
                        const num = parseFloat(value) || 0;
                        return { min: num, max: num, unit: unit?.trim() || "" };
                      }
                    });

                // ---------- Parse Datasheet Values ----------
                const dsRanges = parseRanges(datasheetDetails?.ranges);
                const dsUnit = getUnit(datasheetDetails?.ranges);

                // ---------- Parse Option Values ----------
                const optionRanges = parseRanges(option.masterrange);
                const optionType = option.type;

                // ---------- Validation Logic ----------
                let isInvalid = false;
                let invalidReason = "";
                let skipRangeCheck = false; 

                if(optionType == 3 || optionType == 2){
                 skipRangeCheck = true;
                invalidReason = `Monitoring Master`;
              }

              const missing = [];

                if (!dsRanges || dsRanges.length === 0) {
                  missing.push("DUC Range");
                }

                if (!optionRanges || optionRanges.length === 0) {
                  missing.push("Master Range ");
                }

                if (missing.length > 0) {
                  skipRangeCheck = true;
                  invalidReason = `Not validated: Missing ${missing.join(" and ")}`;
                }

                if (dsRanges.length && optionRanges.length && !skipRangeCheck) {
                  const dsMins = dsRanges.map((r) => r.min);
                  const dsMaxs = dsRanges.map((r) => r.max);
                  const minDS = Math.min(...dsMins);
                  const maxDS = Math.max(...dsMaxs);
                  const dsRangeUnit = dsUnit || optionRanges[0]?.unit;
                  const optUnit = optionRanges[0]?.unit;

              

                  // ⚙️ --- UNIT CHECK ---
                  if (optUnit && dsRangeUnit && optUnit !== dsRangeUnit) {
                    const baseOpt = getBaseUnit(optUnit);
                    const baseDS = getBaseUnit(dsRangeUnit);
                       

                    if (baseOpt !== baseDS) {
                    // ❌ Base units differ — skip range validation, but not invalid
                      skipRangeCheck = true;
                      invalidReason = `Not validated master (${optUnit} ≠ ${dsRangeUnit})`;
                    } else {
                      // ✅ Same base — convert to datasheet unit
                      optionRanges.forEach((r) => {
                        r.min = unitConvertor(r.min, r.unit, dsRangeUnit);
                        r.max = unitConvertor(r.max, r.unit, dsRangeUnit);
                        r.unit = dsRangeUnit;
                      });
                    }
                  }

                  // ⚖️ --- RANGE CHECK ---
                  if (!isInvalid && !skipRangeCheck) {
                  const hasValidRange = optionRanges.some(({ min, max, unit }) => {
                    if (unit !== dsRangeUnit) return false;
                    if (min == null || max == null) return false;
                    const [low, high] = [Math.min(min, max), Math.max(min, max)];
                    return  maxDS <= high;
                  });
            

                    if (!hasValidRange) {
                            //  console.log({hasValidRange, optionRanges, minDS, maxDS, dsRangeUnit});
                      isInvalid = true;
                      invalidReason = "Range Mismatch";
                    }
                  }
                }

                // ---------- Render Option ----------
                return (
                  <li
                    {...props}
                    style={{
                      color: isInvalid && masterEQPRangeFiltration ? "red" : "inherit",
                      opacity: isInvalid && masterEQPRangeFiltration ? 0.6 : 1,
                      pointerEvents: isInvalid && masterEQPRangeFiltration ? "none" : "auto",
                    }}
                    aria-disabled={isInvalid && masterEQPRangeFiltration}
                    title={(isInvalid && masterEQPRangeFiltration) || (skipRangeCheck && masterEQPRangeFiltration) ? invalidReason : ""}
                  >
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                      disabled={isInvalid && masterEQPRangeFiltration}
                    />
                    {option.stId}: {option.standardName} {option.masterrange ?  `(${option.masterrange?.replaceAll("||", ", ")
                              ?.replaceAll("|", " to ")
                              ?.replaceAll("#", "")})` : ""}
                    {(isInvalid || skipRangeCheck) && masterEQPRangeFiltration && (
                      <span
                        style={{
                          fontSize: "12px",
                          marginLeft: "6px",
                          color: "red",
                        }}
                      >
                         ({invalidReason})
                      </span>
                    )}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label="Master EQP Selection" />
              )}
              onChange={(event, value) => {
                const selectedIds = value.map((option) => option.id)?.join(",");
                dispatch({
                  field: "selectedMastersIds",
                  value: selectedIds,
                });
              }}
            />
          </div>
        )}

        {selectedStandardMasters.length > 0 ? (
          <div style={{ width: "100%", overflow: "auto" }}>
            <ClassicTableDatasheet>
              <Table sx={{ minWidth: 660 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    {selectedStandardMasters &&
                      selectedStandardMasters.length > 0 && (
                        <TableCell>Sr. No.</TableCell>
                      )}
                    {selectedStandardMasters &&
                      selectedStandardMasters.some(
                        (row) => row.standardName
                      ) && <TableCell>Name of Standard</TableCell>}
                    {selectedStandardMasters &&
                      selectedStandardMasters.some(
                        (row) => row?.stId,
                      ) && <TableCell>Identification No.</TableCell>}
                    {selectedStandardMasters &&
                      selectedStandardMasters.some(
                        (row) => row.serialNo,
                      ) && <TableCell>Serial No.</TableCell>}
                    {!props.isPrinting && selectedStandardMasters &&
                      selectedStandardMasters.some(
                        (row) => row.masterrange,
                      ) && <TableCell>Range</TableCell>}
                    {!props.isPrinting && selectedStandardMasters &&
                      selectedStandardMasters.some(
                        (row) => row.masteraccuracy
                      ) && <TableCell>Accuracy</TableCell>}
                    {!props.isPrinting && selectedStandardMasters &&
                      selectedStandardMasters.some(
                        (row) => row.masterleastcount
                      ) && <TableCell>Least Count</TableCell>}
                    {!props.isPrinting &&
                      selectedStandardMasters &&
                      selectedStandardMasters.some((row) => row.make) && (
                        <TableCell>Make/Model</TableCell>
                      )}
                    {selectedStandardMasters &&
                      selectedStandardMasters.some(
                        (row) => row.certificateNumber
                      ) && <TableCell>Certificate No.</TableCell>}
                    {/* {!props.isPrinting &&
                      selectedStandardMasters &&
                      selectedStandardMasters.some(
                        (row) => row.traceability,
                      ) && <TableCell>Calibration Agency</TableCell>} */}
                    {!props.isPrinting && selectedStandardMasters &&
                      selectedStandardMasters.some((row) => row.calDate) && (
                        <TableCell>Calibration Date</TableCell>
                      )}
                    {selectedStandardMasters &&
                      selectedStandardMasters.some((row) => row.validUpto) && (
                        <TableCell>Due Date</TableCell>
                      )}
                      {
                      selectedStandardMasters &&
                      selectedStandardMasters.some(
                        (row) => row.traceability,
                      ) && <TableCell>Traceability with NABL Lab No</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedStandardMasters &&
                    selectedStandardMasters?.map((row, index) => (
                      <TableRow key={"standard-row-" + index}>
                        {selectedStandardMasters.length > 0 && (
                          <TableCell>{index + 1}</TableCell>
                        )}
                        {selectedStandardMasters.some(
                          (row) => row.standardName
                        ) && (
                          <TableCell
                            style={{
                              color: `${
                                moment().isAfter(row?.validUpto) ? "red" : ""
                              }`,
                            }}
                          >
                            {row.standardName}
                          </TableCell>
                        )}
                        {selectedStandardMasters.some(
                          (row) => row.stId,
                        ) && (
                          <TableCell>
                            {row.stId}
                          </TableCell>
                        )}
                        {selectedStandardMasters.some(
                          (row) => row.serialNo,
                        ) && (
                          <TableCell>
                            {row.serialNo}
                          </TableCell>
                        )}
                        {!props.isPrinting && selectedStandardMasters.some(
                          (row) => row.masterrange,
                        ) && (
                          <TableCell>{parseRange(row.masterrange)}</TableCell>
                        )}
                        {!props.isPrinting && selectedStandardMasters.some(
                          (row) => row.masteraccuracy,
                        ) && (
                          <TableCell>
                            {accuracyFormat(row.masteraccuracy)}
                          </TableCell>
                        )}
                        {!props.isPrinting && selectedStandardMasters.some(
                          (row) => row.masterleastcount,
                        ) && (
                          <TableCell>
                            {parseRange(row.masterleastcount)}
                          </TableCell>
                        )}
                        {!props.isPrinting &&
                          selectedStandardMasters.some((row) => row.make) && (
                            <TableCell>{row.make}</TableCell>
                          )}
                        {selectedStandardMasters.some(
                          (row) => row.certificateNumber
                        ) && <TableCell>{row.certificateNumber}</TableCell>}
                        {/* {!props.isPrinting &&
                          selectedStandardMasters.some(
                            (row) => row.traceability,
                          ) && <TableCell>{row.traceability}</TableCell>} */}
                        {!props.isPrinting && selectedStandardMasters.some((row) => row.calDate) && (
                          <TableCell>
                            {moment(row.calDate).format("DD/MM/YYYY")}
                          </TableCell>
                        )}
                        {selectedStandardMasters.some(
                          (row) => row.validUpto
                        ) && (
                          <TableCell>
                            {moment(row.validUpto).format("DD/MM/YYYY")}
                          </TableCell>
                        )}
                        {
                          selectedStandardMasters.some(
                            (row) => row.traceability,
                          ) && <TableCell>{row.traceability}</TableCell>}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </ClassicTableDatasheet>
          </div>
        ) : null}
      </>
    </div>
  );
}
